import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  Pagination,
  Stack,
} from '@mui/material';
import {
  Close as CloseIcon,
  Storage,
  Computer,
  CloudUpload,
  Dataset,
  Apps,
  ChevronLeft,
  ChevronRight,
  FolderShared,
} from '@mui/icons-material';
import { API_CONFIG, API_ENDPOINTS, STORAGE_KEYS, isDriveAppKey, shouldShowAppInMarketplace } from '../helpers/constants';
import { LanguageConstants } from '../helpers/LanguageConstants';
import { useTheme } from '@mui/material/styles';
import { formatDisplayDateTime } from '../helpers/dateUtils';

interface DataSource {
  id: string;
  name: string;
  storage_type: string;
  provider?: string | null;
  app_type?: string | null;
  database_type?: string | null;
  config?: {
    database_connection?: any;
    [key: string]: any;
  };
  added_by_user?: {
    user_id: string;
    name: string;
    email_id: string;
  };
  is_connected: boolean;
  created_at?: string;
}

interface DataSourcesResponse {
  datasources: DataSource[];
  total: number;
  page: number;
  size: number;
  pages: number;
}

interface ConnectedDatasourcesCountProps {
  channelId?: string;
  tabValue: number; // 0: Device, 1: Cloud, 2: Database, 3: Drives, 4: Connect App
  refreshTrigger?: number; // Increment this to trigger a refresh
  variant?: 'tile' | 'footer'; // 'tile' for fixed positioning, 'footer' for footer placement
  hasBackButton?: boolean; // Whether back button is visible (for Drives/Connect App tab Google Drive flow)
}

const ConnectedDatasourcesCount: React.FC<ConnectedDatasourcesCountProps> = ({
  channelId,
  tabValue,
  refreshTrigger = 0,
  variant = 'tile',
  hasBackButton = false,
}) => {
  const [connectedDatasources, setConnectedDatasources] = useState<DataSource[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [page, setPage] = useState<number>(1);
  const [pageSize] = useState<number>(10);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [dialogLoading, setDialogLoading] = useState(false);
  const muiTheme = useTheme();

  // Filter datasources based on tab type
  // Client-side filtering for Cloud tab to separate local files with provider vs without provider
  const filterDatasourcesByTab = (datasources: DataSource[]): DataSource[] => {
    return datasources.filter((ds) => {
      switch (tabValue) {
        case 0: // Device tab - local storage, no provider/app_type (or null values)
          return ds.storage_type === 'local' && 
                 (ds.provider === null || ds.provider === undefined || ds.provider === '') &&
                 (ds.app_type === null || ds.app_type === undefined || ds.app_type === '');
        
        case 1: // Cloud tab - local storage with cloud provider (S3, etc.)
          // API filters by storage_type: 'cloud', but we need to check if it's actually
          // local with provider, or if API returns cloud type directly
          // For now, accept both cases
          return (ds.storage_type === 'local' && 
                 ds.provider !== null && 
                 ds.provider !== undefined && 
                 ds.provider !== '') ||
                 ds.storage_type === 'cloud';
        
        case 3: // Drives tab - count connected drive integrations regardless of marketplace visibility
          return ds.storage_type === 'app' &&
                 ds.app_type !== null &&
                 ds.app_type !== undefined &&
                 ds.app_type !== '' &&
                 isDriveAppKey(ds.app_type);

        case 4: // Connect App tab - app storage type with non-drive app_type
          return ds.storage_type === 'app' &&
                 ds.app_type !== null &&
                 ds.app_type !== undefined &&
                 ds.app_type !== '' &&
                 !isDriveAppKey(ds.app_type) &&
                 shouldShowAppInMarketplace(ds.app_type);
        
        case 2: // Database tab
          return ds.storage_type === 'database';
        default:
          return false;
      }
    });
  };

  // Get storage_type filter based on tab
  const getStorageTypeFilter = (): string => {
    switch (tabValue) {
      case 0: // Device tab
        return 'local';
      case 1: // Cloud tab
        return 'cloud';
      case 2: // Database tab
        return 'database';
      case 3: // Drives tab
        return 'app';
      case 4: // Connect App tab
        return 'app';
      default:
        return 'local'; // Default to local
    }
  };

  // Fetch datasources for count (no pagination)
  const fetchDatasources = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      const params = new URLSearchParams({
        storage_type: getStorageTypeFilter(),
      });

      if (channelId) {
        params.append('channel_id', channelId);
      }

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DataSourcesResponse = await response.json();
      // Store the total count from API response
      setTotalCount(data.total || 0);
    } catch (err) {
      console.error('Error fetching datasources:', err);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch datasources for dialog with pagination - memoized with useCallback
  const fetchDatasourcesForDialog = useCallback(async (currentPage: number = 1) => {
    try {
      setDialogLoading(true);
      const token = localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) || '';
      
      const params = new URLSearchParams({
        storage_type: getStorageTypeFilter(),
        page: currentPage.toString(),
        size: pageSize.toString(),
      });

      if (channelId) {
        params.append('channel_id', channelId);
      }

      const response = await fetch(
        `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.DATASOURCES.LIST}?${params}`,
        {
          method: 'GET',
          headers: {
            accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: DataSourcesResponse = await response.json();
      // Debug: Log database datasources to see their structure
      if (tabValue === 2) { // Database tab
        console.log('Database datasources from API:', data.datasources);
      }
      // Apply client-side filtering for Cloud tab (to separate local with provider vs without)
      const filtered = filterDatasourcesByTab(data.datasources);
      setConnectedDatasources(filtered);
      setTotalPages(data.pages || 0);
    } catch (err) {
      console.error('Error fetching datasources for dialog:', err);
      setConnectedDatasources([]);
      setTotalPages(0);
    } finally {
      setDialogLoading(false);
    }
  }, [channelId, pageSize, tabValue]);

  useEffect(() => {
    fetchDatasources();
  }, [channelId, tabValue, refreshTrigger]);

  // Reset to page 1 when dialog opens or tab changes
  useEffect(() => {
    if (openDialog) {
      setPage(1);
    }
  }, [openDialog, tabValue]);

  // Fetch datasources when dialog opens or page changes
  useEffect(() => {
    if (openDialog) {
      fetchDatasourcesForDialog(page);
    }
  }, [openDialog, page, fetchDatasourcesForDialog]);

  const getDatasourceType = (ds: DataSource): string => {
    // Handle database types
    if (ds.storage_type === 'database') {
      // Check for database_type field (could be top-level or in config)
      let dbType = ds.database_type || ds.config?.database_type;
      
      // If not found, try to extract from name as fallback
      if (!dbType && ds.name) {
        const nameLower = ds.name.toLowerCase();
        // Check for common database type patterns in the name
        if (nameLower.includes('mysql')) {
          dbType = 'mysql';
        } else if (nameLower.includes('mariadb')) {
          dbType = 'mariadb';
        } else if (nameLower.includes('postgres') || nameLower.includes('postgresql')) {
          dbType = 'postgresql';
        } else if (nameLower.includes('oracle')) {
          dbType = 'oracle';
        } else if (nameLower.includes('dynamodb') || nameLower.includes('dynamo')) {
          dbType = 'dynamodb';
        } else if (nameLower.includes('redshift')) {
          dbType = 'redshift';
        } else if (nameLower.includes('snowflake')) {
          dbType = 'snowflake';
        } else if (nameLower.includes('bigquery') || nameLower.includes('big query')) {
          dbType = 'bigquery';
        }
      }
      
      if (dbType) {
        // Format database type names nicely
        // Handle different formats: "BigQuery", "bigquery", "postgresql", etc.
        let formattedType = dbType.toString();
        
        // Replace underscores with spaces
        formattedType = formattedType.replace(/_/g, ' ');
        
        // Capitalize first letter of each word
        formattedType = formattedType.replace(/\b\w/g, (l: string) => l.toUpperCase());
        
        // Handle special cases
        if (formattedType.toLowerCase() === 'bigquery') {
          return 'BigQuery';
        }
        if (formattedType.toLowerCase() === 'dynamodb') {
          return 'DynamoDB';
        }
        if (formattedType.toLowerCase() === 'mariadb') {
          return 'MariaDB';
        }
        if (formattedType.toLowerCase() === 'postgresql') {
          return 'PostgreSQL';
        }
        if (formattedType.toLowerCase() === 'mysql') {
          return 'MySQL';
        }
        if (formattedType.toLowerCase() === 'oracle') {
          return 'Oracle';
        }
        if (formattedType.toLowerCase() === 'redshift') {
          return 'Redshift';
        }
        if (formattedType.toLowerCase() === 'snowflake') {
          return 'Snowflake';
        }
        
        return formattedType;
      }
      // Fallback to "Database" if type not found
      return 'Database';
    }
    
    // Handle app storage types
    if (ds.storage_type === 'app' && ds.app_type) {
      return ds.app_type.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
    
    // Handle cloud/local storage with provider
    if ((ds.storage_type === 'local' || ds.storage_type === 'cloud') && ds.provider) {
      // Handle provider names: s3, azure, gcp
      return ds.provider.toUpperCase();
    }
    
    // Default fallback
    return 'Local File';
  };

  const count = totalCount;

  // Get icon based on tab type
  const getTabIcon = () => {
    switch (tabValue) {
      case 0: // Device tab
        return <Computer />;
      case 1: // Cloud tab
        return <CloudUpload />;
      case 2: // Database tab
        return <Storage />;
      case 3: // Drives tab
        return <FolderShared />;
      case 4: // Connect App tab
        return <Apps />;
      default:
        return <Storage />;
    }
  };

  // Only adjust position for Drives or Connect App tabs (tabValue === 3 or 4) when back button is visible
  const shouldAdjustForBackButton = variant === 'footer' && (tabValue === 3 || tabValue === 4) && hasBackButton;
  
  const tileStyles = variant === 'footer' 
    ? {
        position: 'absolute' as const,
        bottom: 12,
        left: shouldAdjustForBackButton ? 120 : 16, // Adjust only when back button is visible on Apps tab
        zIndex: 1,
      }
    : {
        position: 'fixed' as const,
        bottom: 16,
        left: 16,
        zIndex: 1400,
      };

  return (
    <>
      {/* Count Tile - Bottom Left */}
      <Paper
        onClick={() => setOpenDialog(true)}
        sx={{
          ...tileStyles,
          padding: '8px 12px',
          cursor: 'pointer',
          backgroundColor: muiTheme.palette.background.paper,
          border: `1px solid ${muiTheme.palette.divider}`,
          borderRadius: 1,
          boxShadow: muiTheme.palette.mode === 'dark' 
            ? '0 2px 4px rgba(0,0,0,0.2)' 
            : '0 1px 3px rgba(0,0,0,0.08)',
          transition: 'all 0.2s ease',
          '&:hover': {
            boxShadow: muiTheme.palette.mode === 'dark'
              ? '0 4px 8px rgba(0,0,0,0.3)'
              : '0 2px 6px rgba(0,0,0,0.12)',
            transform: 'translateY(-1px)',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box
            sx={{ 
              color: muiTheme.palette.primary.main,
              fontSize: 18,
              display: 'flex',
              alignItems: 'center',
            }} 
          >
            {getTabIcon()}
          </Box>
          {loading ? (
            <CircularProgress size={14} />
          ) : (
            <Typography variant="body2" sx={{ fontSize: '0.875rem', whiteSpace: 'nowrap' }}>
              Connected {LanguageConstants.datasource}: {count}
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Dialog to show connected datasources */}
      <Dialog
        open={openDialog}
        onClose={() => setOpenDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: muiTheme.palette.background.paper,
          },
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          borderBottom: `1px solid ${muiTheme.palette.divider}`,
          pb: 2,
          px: 3,
          pt: 2.5,
        }}>
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Connected {LanguageConstants.datasource_s} ({count})
          </Typography>
          <IconButton
            onClick={() => setOpenDialog(false)}
            size="small"
            sx={{ 
              color: muiTheme.palette.text.secondary,
              '&:hover': {
                backgroundColor: muiTheme.palette.action.hover,
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ px: 3, pt: 4, pb: 2 }}>
          {connectedDatasources.length === 0 && !dialogLoading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No connected {LanguageConstants.datasource_s.toLowerCase()} found for this tab.
              </Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mt: 3 }}>
                <TableContainer 
                    sx={{ 
                      maxHeight: 400,
                      overflow: 'auto',
                      '& .MuiTableHead-root': {
                        position: 'sticky',
                        top: 0,
                        zIndex: 10,
                      },
                      '&::-webkit-scrollbar': {
                        width: '8px',
                        height: '8px',
                      },
                      '&::-webkit-scrollbar-track': {
                        backgroundColor: muiTheme.palette.mode === 'dark' 
                          ? muiTheme.palette.grey[800] 
                          : muiTheme.palette.grey[100],
                        borderRadius: '4px',
                      },
                      '&::-webkit-scrollbar-thumb': {
                        backgroundColor: muiTheme.palette.mode === 'dark' 
                          ? muiTheme.palette.grey[600] 
                          : muiTheme.palette.grey[400],
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: muiTheme.palette.mode === 'dark' 
                            ? muiTheme.palette.grey[500] 
                            : muiTheme.palette.grey[500],
                        },
                      },
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead
                        sx={{
                          '& .MuiTableCell-head': {
                            backgroundColor: muiTheme.palette.mode === 'dark' 
                              ? muiTheme.palette.grey[800] 
                              : muiTheme.palette.grey[50],
                          },
                        }}
                      >
                        <TableRow>
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.primary.light 
                                : 'rgb(28, 171, 222)',
                              borderBottom: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.divider : '#e0e0e0'}`,
                              py: 1.5,
                              px: 2,
                              backgroundColor: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.grey[800] 
                                : muiTheme.palette.grey[50],
                            }}
                          >
                            Name
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.primary.light 
                                : 'rgb(28, 171, 222)',
                              borderBottom: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.divider : '#e0e0e0'}`,
                              py: 1.5,
                              px: 2,
                              backgroundColor: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.grey[800] 
                                : muiTheme.palette.grey[50],
                            }}
                          >
                            Type
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.primary.light 
                                : 'rgb(28, 171, 222)',
                              borderBottom: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.divider : '#e0e0e0'}`,
                              py: 1.5,
                              px: 2,
                              backgroundColor: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.grey[800] 
                                : muiTheme.palette.grey[50],
                            }}
                          >
                            Added By
                          </TableCell>
                          <TableCell 
                            sx={{ 
                              fontWeight: 700,
                              fontSize: '0.875rem',
                              color: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.primary.light 
                                : 'rgb(28, 171, 222)',
                              borderBottom: `1px solid ${muiTheme.palette.mode === 'dark' ? muiTheme.palette.divider : '#e0e0e0'}`,
                              py: 1.5,
                              px: 2,
                              backgroundColor: muiTheme.palette.mode === 'dark' 
                                ? muiTheme.palette.grey[800] 
                                : muiTheme.palette.grey[50],
                            }}
                          >
                            Added On
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                  {connectedDatasources.map((ds) => (
                    <TableRow 
                      key={ds.id} 
                      hover
                      sx={{
                        '&:last-child td': { borderBottom: 0 },
                        '&:hover': {
                          backgroundColor: muiTheme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ds.name}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Chip
                          label={getDatasourceType(ds)}
                          size="small"
                          sx={{
                            backgroundColor: muiTheme.palette.primary.light,
                            color: muiTheme.palette.common.white,
                            fontSize: '0.75rem',
                            height: 24,
                            fontWeight: 500,
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.25 }}>
                            {ds.added_by_user?.name || 'Unknown'}
                          </Typography>
                          {ds.added_by_user?.email_id && (
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block',
                                fontSize: '0.75rem',
                              }}
                            >
                              {ds.added_by_user.email_id}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ py: 1.5, px: 2 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {ds.created_at ? formatDisplayDateTime(ds.created_at) : 'Unknown'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              
              {/* Pagination Controls with Loading */}
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3, mb: 1 }}>
                {dialogLoading && (
                  <CircularProgress size={20} />
                )}
                {totalPages > 1 && (
                  <Stack spacing={2}>
                    <Pagination
                      count={totalPages}
                      page={page}
                      onChange={(event, value) => setPage(value)}
                      color="primary"
                      size="medium"
                      showFirstButton
                      showLastButton
                      siblingCount={1}
                      boundaryCount={1}
                      disabled={dialogLoading}
                    />
                  </Stack>
                )}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ConnectedDatasourcesCount;

